import {
  Getters,
  Mutations,
  Module,
  createMapper,
  Actions
} from 'vuex-smart-module'
import _ from 'lodash'

import { Vue } from 'vue-property-decorator'
import { roles } from '@/config/roles'
import * as Sentry from '@sentry/vue'
import BuildInfoAPI from '@/components/routes/admin/build-info/helpers/requests/buildInfoAPI'
import { BuildInfo } from 'interfaces/build-info'
import UsersAPI from '@/api/requests/users'
import UserAPI from '@/api/requests/user'
import ProfileAPI from '@/components/routes/profile/helpers/requests'

class UserState {
  user: any = null
  clientVersion: BuildInfo | null = null
  isBadLogin = false
}

class UserGetters extends Getters<UserState> {
  get isAuthenticated() {
    return !_.isNil(this.state.user)
  }

  get currentUser() {
    return this.state.user
  }

  get userRoles() {
    return this.state.user?.roles ?? []
  }

  get isAuthor() {
    return this.getters.userRoles.includes(roles.author)
  }

  get isAdmin() {
    return this.getters.userRoles.includes(roles.admin)
  }

  get isEditor() {
    return this.getters.userRoles.includes(roles.editor)
  }

  get isCttMC() {
    return this.getters.userRoles.includes(roles.cttMC)
  }

  get isCttIPC() {
    return this.getters.userRoles.includes(roles.cttIPC)
  }

  get isCttMcOrAdmin() {
    return this.getters.isCttMC || this.getters.isAdmin
  }

  get userDepartments() {
    return this.state.user?.userDepartments ?? []
  }

  get isFakeAuth() {
    return !!this.state.user?.actualUser
  }

  get isDirector() {
    if (this.state.user && this.state.user.userDepartments) {
      for (const department of this.state.user?.userDepartments) {
        const isDirector = !!department.workFunctions.find(
          (o: any) => o.title === 'Заведующий кафедрой' || o.title === 'Декан'
        )
        if (isDirector) {
          return isDirector
        }
      }
    }
    return false
  }

  get canViewOtherUsers() {
    return this.getters.userDepartments.find(
      (dep: any) => dep.requestPermission === true
    )
  }
}

class UserMutations extends Mutations<UserState> {
  setUser(user: any) {
    Vue.set(this.state, 'user', user)
    if (user) {
      Sentry.setUser({
        email: user.email,
        id: String(user.id),
        name: user.name,
        surname: user.surname,
        midname: user.midname
      })
    } else {
      Sentry.setUser(null)
    }
  }
  setClientVersion(data: BuildInfo | null) {
    this.state.clientVersion = data
  }
  setBadLogin(isBadLogin: boolean) {
    Vue.set(this.state, 'isBadLogin', isBadLogin)
  }
}

class UserActions extends Actions<
  UserState,
  UserGetters,
  UserMutations,
  UserActions
> {
  async fetchCurrentUser(reload = false) {
    const [user, client] = await Promise.all([
      UserAPI.getCurrentUser(reload),
      BuildInfoAPI.getClientBuildFromServer()
    ])
    this.mutations.setUser(user.data.user)
    this.mutations.setClientVersion(client.data)
  }

  async fetchUser(userId: number) {
    if (_.isEmpty(this.state.user)) {
      const response = await UsersAPI.getProfile(userId)
      this.mutations.setUser(response.data)
    }
  }

  async updateUserProfile(data: any) {
    if (!_.isEmpty(this.state.user)) {
      if (this.state.user) {
        await ProfileAPI.updateProfile(data)
        this.dispatch('fetchCurrentUser', true)
      }
    }
  }
}

export const user = new Module({
  state: UserState,
  getters: UserGetters,
  mutations: UserMutations,
  actions: UserActions
})

export const userMappers = createMapper(user)
